import { apiClient } from "./apiClient";
import { endpoints } from "./endpoints";
import type { ApiRecord } from "@/types/api";

/**
 * Backend v2.1 (Sprint 4.3) Reporting Read Model. `getAvailableReports`
 * lists every already-persisted report's metadata for a `portfolio_id`
 * (`ReportSummaryDto[]`); `getReportMetadata` reads one report type's
 * metadata (`ReportMetadataDto`) - neither generates a report. Only
 * report types that have actually been generated appear in the list
 * (an honest empty list, not five hardcoded "unavailable" placeholders).
 * `generatePortfolioReport` wraps the one write path (moved to
 * `POST /reports/generate` in Sprint 4.3, still only the Portfolio
 * Report use case, TD-099) for completeness - not invoked by any page.
 */
export const reportingService = {
  async getAvailableReports(portfolioId: string): Promise<ApiRecord[]> {
    const response = await apiClient.get<ApiRecord[]>(endpoints.reports.root, {
      params: { portfolio_id: portfolioId },
    });
    return response.data;
  },
  async getReportMetadata(portfolioId: string, reportName: string): Promise<ApiRecord> {
    const response = await apiClient.get<ApiRecord>(endpoints.reports.byName(reportName), {
      params: { portfolio_id: portfolioId },
    });
    return response.data;
  },
  async generatePortfolioReport(params: PortfolioReportGenerationParams): Promise<ApiRecord> {
    const response = await apiClient.post<ApiRecord>(endpoints.reports.generate, undefined, {
      params: {
        portfolio_id: params.portfolioId,
        report_id: params.reportId,
        starting_cash: params.startingCash,
      },
    });
    return response.data;
  },
};

export interface PortfolioReportGenerationParams {
  portfolioId: string;
  reportId: string;
  startingCash?: number;
}
